import { useCallback, useMemo, useRef } from 'react';
import * as Comlink from 'comlink';

import { useApi } from '@/shared/api';
import { apiConfig } from '@/shared/api/config';
import { WorkerMethods } from '@/shared/lib/crypto/worker';
import { createCryptoWorker } from '@/shared/lib/crypto/worker';
import { CryptoCore } from '@/shared/lib/secure-json/core/crypto-core';
import { Permission } from '@/shared/lib/secure-json/core/secure-json-collection/types';
import { CollectionName } from '@/shared/lib/sj-orm/constants';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { EncryptedFileKeyDto } from '@/shared/lib/sj-orm/models/encrypted-file-key.dto';
import { FileMetaInfoDto } from '@/shared/lib/sj-orm/models/file-meta-info.dto';
import { useAssistantStore } from '@/shared/store/assistant.store';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { useProfileStore } from '@/shared/store/profile.store';

type EncryptAndUploadParams = {
  publicKey?: string | (string | null)[] | null;
  itemData?: Partial<FileMetaInfoDto>;
  withoutMetaInfo?: boolean;
};

export type FullyDeleteParams = {
  id?: string;
  filePath?: string;
};

export type FilePath = string;

export type useFileEncryptionUploadOptions = {
  withWorker?: boolean;
  useDirectUrl?: boolean;
};

export const useFileEncryptionUpload = ({
  withWorker,
  useDirectUrl,
}: useFileEncryptionUploadOptions = {}) => {
  const api = useApi();
  const sjDatabase = useSJDatabase();
  const { keyPair } = useKeyPairStore();
  const { publicKey, isAssistant } = useProfileStore();
  const { requestId } = useAssistantStore();
  const workerRef = useRef<Comlink.Remote<WorkerMethods> | null>(null);

  if (withWorker && !workerRef.current && typeof Worker !== 'undefined') {
    const worker = createCryptoWorker();
    workerRef.current = Comlink.wrap<WorkerMethods>(worker);
  }

  const baseUrl = useDirectUrl ? apiConfig.directUrl : apiConfig.gtwUrl;

  const encryptAndUploadFile = useCallback(
    async (file: File | null, options?: EncryptAndUploadParams): Promise<FilePath | null> => {
      if (!file || !keyPair) {
        throw new Error('File or keyPair is not defined');
      }

      const additionalPublicKey = options?.publicKey;
      const itemData = options?.itemData;
      const symmetricKey = CryptoCore.aes.generateKey();

      const encryptFile = workerRef.current
        ? workerRef.current.encryptFile
        : CryptoCore.files.encrypt;

      const encryptedFile = await encryptFile(file, symmetricKey);
      const resUpload = await api.filesUploadFile(
        {
          fileContent: encryptedFile,
        },
        { baseUrl, format: 'text' },
      );

      if (!resUpload.data) return null;

      const newFileDto: EncryptedFileKeyDto = {
        id: `${resUpload.data}:${
          requestId
            ? sjDatabase.collections.delegatingRequests
                ?.getPublicKeys(requestId)
                ?.find((key) => key !== keyPair?.publicKey)
            : keyPair.publicKey
        }`, // format: <filePath>:<publicKey>
        symmetricKey,
      };

      if (isAssistant) {
        newFileDto.isCreatedByAssistant = true;
      }

      const newEncryptedFileKey = await sjDatabase.collections.encryptedFileKey?.create(
        newFileDto,
        [
          keyPair.publicKey,
          ...(requestId && [
            sjDatabase.collections.delegatingRequests
              ?.getPublicKeys(requestId)
              ?.find((key) => key !== keyPair?.publicKey) ?? '',
          ]),
        ],
      );

      const keyArray = Array.isArray(additionalPublicKey)
        ? additionalPublicKey
        : [additionalPublicKey];

      if (keyArray?.length && publicKey && newEncryptedFileKey) {
        for (const additionalKey of keyArray) {
          if (!additionalKey) continue;
          await sjDatabase.collections.encryptedFileKey?.putPublicKey(
            newEncryptedFileKey.id,
            additionalKey,
          );

          sjDatabase.collections.encryptedFileKey?.putPermissions(
            newEncryptedFileKey.id,
            publicKey,
            [Permission.WRITE, Permission.READ],
          );

          sjDatabase.collections.encryptedFileKey?.putPermission(
            newEncryptedFileKey.id,
            additionalKey,
            Permission.READ,
          );
        }
      }

      if (options?.withoutMetaInfo) {
        return resUpload.data;
      }

      let fileMetaInfo: FileMetaInfoDto = {
        id: sjDatabase.generateId(CollectionName.FILE_META_INFO),
        name: file.name,
        extension: `.${file.name.split('.').pop() ?? 'unknown'}`,
        filePath: resUpload.data,
        size: file.size,
      };

      if (itemData) {
        fileMetaInfo = { ...fileMetaInfo, ...itemData };
      }

      if (isAssistant) {
        fileMetaInfo.isCreatedByAssistant = true;
      }

      await sjDatabase.collections.fileMetaInfos?.create(fileMetaInfo, [
        keyPair.publicKey,
        ...keyArray.filter((item) => item !== null && item !== undefined),
        ...(requestId && [
          sjDatabase.collections.delegatingRequests
            ?.getPublicKeys(requestId)
            ?.find((key) => key !== keyPair?.publicKey) ?? '',
        ]),
      ]);

      if (keyArray?.length && publicKey && newEncryptedFileKey) {
        for (const additionalKey of keyArray) {
          if (!additionalKey) continue;
          await sjDatabase.collections.fileMetaInfos?.putPublicKey(fileMetaInfo.id, additionalKey);

          sjDatabase.collections.fileMetaInfos?.putPermissions(fileMetaInfo.id, publicKey, [
            Permission.WRITE,
            Permission.READ,
          ]);

          sjDatabase.collections.fileMetaInfos?.putPermission(
            fileMetaInfo.id,
            additionalKey,
            Permission.READ,
          );
        }
      }

      return resUpload.data;
    },
    [keyPair, sjDatabase, api],
  );

  const deleteUploadedFile = useCallback(
    (encryptedFileKeyId: string) => {
      sjDatabase.collections.encryptedFileKey?.removeById(encryptedFileKeyId);
      // format: <filePath>:<publicKey>
      const filePath = encryptedFileKeyId.split(':')[0];

      return api.filesDeleteFile(filePath);
    },
    [sjDatabase, api],
  );

  const deleteUploadedFileByPath = useCallback(
    (filePath: string) => api.filesDeleteFile(filePath),
    [sjDatabase, api],
  );

  const getFileMeta = useCallback(
    (id: string) => {
      return sjDatabase.collections.fileMetaInfos?.findOne((item) => item.id === id);
    },
    [sjDatabase],
  );

  const findFileMetaByPath = useCallback(
    (filePath: string) => {
      return sjDatabase.collections.fileMetaInfos?.findOne((item) => item.filePath === filePath);
    },
    [sjDatabase],
  );

  const fullyDeleteUploadedFile = useCallback(
    (params: FullyDeleteParams) => {
      if (!keyPair || !params.filePath) {
        return Promise.reject();
      }

      if ('filePath' in params) {
        const metaInfo = findFileMetaByPath(params.filePath);
        if (!metaInfo) return Promise.reject();

        sjDatabase.collections.fileMetaInfos?.removeById(metaInfo.id);
        const encryptedFileKeyId = `${params.filePath}:${keyPair.publicKey}`;

        sjDatabase.collections.encryptedFileKey?.removeById(encryptedFileKeyId);
        return deleteUploadedFile(encryptedFileKeyId);
      }

      return Promise.reject();
    },
    [keyPair, findFileMetaByPath, sjDatabase, deleteUploadedFile],
  );

  return useMemo(
    () => ({
      encryptAndUploadFile,
      getFileMeta,
      findFileMetaByPath,
      deleteUploadedFile,
      deleteUploadedFileByPath,
      fullyDeleteUploadedFile,
    }),
    [
      encryptAndUploadFile,
      getFileMeta,
      findFileMetaByPath,
      deleteUploadedFile,
      deleteUploadedFileByPath,
      fullyDeleteUploadedFile,
    ],
  );
};
